import React, { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { getLossGuide } from "@/data/lossCategoryGuides";

const REPORT_STATUSES = ["submitted", "under_review", "verified", "rejected", "published"];
const LEGAL_STATUSES = ["submitted", "in_review", "in_process", "resolved"];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
}

function DetailRow({ label, value, mono }) {
  if (value == null || value === "") return null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-white/45 font-mono-alt">{label}</div>
      <div className={`text-sm text-white/85 mt-0.5 break-words ${mono ? "font-mono-alt text-xs" : ""}`}>{value}</div>
    </div>
  );
}

function CaseDetails({ caseType, c }) {
  const files = c.files || [];
  const guide = c.loss_category ? getLossGuide(c.loss_category) : null;
  const detailLabels = Object.fromEntries((guide?.formFields || []).map((f) => [f.key, f.label]));
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3">
      <h4 className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono-alt">Case details</h4>
      {caseType === "scam_report" ? (
        <div className="grid sm:grid-cols-2 gap-3">
          <DetailRow label="Scam type" value={c.scam_type} />
          <DetailRow label="Amount lost" value={c.amount_lost != null ? `₹${Number(c.amount_lost).toLocaleString("en-IN")}` : null} />
          <DetailRow label="Platform" value={c.platform} />
          <DetailRow label="Telegram" value={c.telegram_username} mono />
          <DetailRow label="UPI" value={c.upi_id} mono />
          <DetailRow label="Phone" value={c.phone} />
          <DetailRow label="Company" value={c.company_name} />
          <DetailRow label="Website" value={c.website} />
          <DetailRow label="Contact email" value={c.contact_email} />
          <DetailRow label="Submitted" value={fmtDate(c.created_at)} />
          <div className="sm:col-span-2">
            <DetailRow label="Description" value={c.description} />
          </div>
          {files.length > 0 && (
            <div className="sm:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-white/45 font-mono-alt mb-1">Evidence files</div>
              <ul className="space-y-1">
                {files.map((f, i) => (
                  <li key={i}>
                    <a
                      href={`${API_BASE}/files/${f.storage_path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-[#D4AF37] hover:underline"
                    >
                      {f.filename || f.original_filename || f.storage_path}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          <DetailRow label="Name" value={c.name} />
          <DetailRow label="Email" value={c.email} />
          <DetailRow label="Phone" value={c.phone} />
          <DetailRow label="Loss type" value={c.loss_type} />
          <DetailRow label="Category" value={c.loss_category} />
          <DetailRow label="Amount" value={c.amount_lost != null ? `₹${Number(c.amount_lost).toLocaleString("en-IN")}` : null} />
          <DetailRow label="Submitted" value={fmtDate(c.created_at)} />
          <DetailRow label="Agent callback" value={c.call_from_agent ? "Yes" : "No"} />
          {c.category_details && Object.entries(c.category_details).map(([k, v]) => (
            v != null && v !== "" ? (
              <DetailRow key={k} label={detailLabels[k] || k.replace(/_/g, " ")} value={String(v)} />
            ) : null
          ))}
          <div className="sm:col-span-2">
            <DetailRow label="Description" value={c.description} />
          </div>
          <div className="sm:col-span-2">
            <DetailRow label="Summary" value={c.summary || c.message} />
          </div>
        </div>
      )}
    </div>
  );
}

function StatusTimeline({ timeline, statuses }) {
  const statusEvents = (timeline || []).filter((ev) => ev.event_type === "status_change");
  const created = (timeline || []).find((ev) => ev.event_type === "created");

  if (statusEvents.length === 0 && !created) {
    return <p className="text-white/45 text-sm">No status history yet.</p>;
  }

  const rows = [];
  if (created) {
    rows.push({
      key: created.id,
      status: created.to_status || "submitted",
      at: created.created_at,
      message: created.message,
      actor: created.actor_name,
    });
  }
  statusEvents.forEach((ev) => {
    rows.push({
      key: ev.id,
      status: ev.to_status || ev.message,
      at: ev.created_at,
      message: ev.message,
      actor: ev.actor_name,
    });
  });

  return (
    <ol className="relative border-l border-[#D4AF37]/30 ml-2 space-y-4 pl-6">
      {rows.map((row, idx) => (
        <li key={row.key} className="relative">
          <span
            className={`absolute -left-[1.55rem] top-1 h-3 w-3 rounded-full ring-2 ring-[#0c0c0c] ${
              idx === rows.length - 1 ? "bg-[#D4AF37]" : "bg-white/30"
            }`}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30 capitalize">
              {(row.status || "").replace(/_/g, " ")}
            </Badge>
            <span className="text-[10px] text-white/45 font-mono-alt">{fmtDate(row.at)}</span>
          </div>
          <p className="text-sm text-white/75 mt-1">{row.message}</p>
          {row.actor && (
            <p className="text-[10px] text-white/40 mt-0.5 font-mono-alt">by {row.actor}</p>
          )}
        </li>
      ))}
    </ol>
  );
}

const CaseWorkspace = ({ caseType, caseId, onUpdated }) => {
  const { user } = useAuth();
  const { t } = useT();
  const cw = t.caseWorkspace || {};
  const checklistLabels = cw.checklist || {};
  const isStaff = user?.role === "admin" || user?.role === "reviewer";
  const [data, setData] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [tagsInput, setTagsInput] = useState("");
  const [commentBody, setCommentBody] = useState("");
  const [commentVis, setCommentVis] = useState(isStaff ? "public" : "public");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const res = await api.get(`/cases/${caseType}/${caseId}`);
      setData(res.data);
      setTagsInput((res.data?.case?.tags || []).join(", "));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [caseType, caseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isStaff) return;
    api.get("/admin/audit/assignees").then((r) => setAssignees(r.data || [])).catch(() => {});
  }, [isStaff]);

  const patchCase = async (body) => {
    setSaving(true);
    try {
      const payload = { ...body };
      if (payload.status === "published" && caseType === "scam_report") {
        payload.is_published = true;
      }
      await api.patch(`/cases/${caseType}/${caseId}`, payload);
      toast.success(cw.caseUpdated || "Case updated");
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const postComment = async () => {
    const text = commentBody.trim();
    if (!text) {
      toast.error("Enter a comment");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/cases/${caseType}/${caseId}/comments`, {
        message: text,
        visibility: isStaff ? commentVis : "public",
      });
      setCommentBody("");
      toast.success(cw.commentPosted || "Comment posted");
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleChecklist = async (key, uploaded) => {
    try {
      await api.patch(`/cases/${caseType}/${caseId}/checklist/${key}`, { uploaded });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="py-8 text-center text-white/50">{cw.loading || "Loading case…"}</div>;
  if (!data?.case) return <div className="py-8 text-center text-white/50">{cw.notFound || "Case not found"}</div>;

  const { case: c, timeline = [], comments = [] } = data;
  const statuses = caseType === "scam_report" ? REPORT_STATUSES : LEGAL_STATUSES;
  const publicComments = comments.filter((cm) => cm.visibility === "public");
  const internalComments = comments.filter((cm) => cm.visibility === "internal");

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto overflow-x-hidden pr-1 min-w-0">
      <div className="flex flex-wrap gap-2 items-center">
        <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 capitalize">
          {(c.status || "").replace(/_/g, " ")}
        </Badge>
        {(c.tags || []).map((tag) => (
          <Badge key={tag} variant="outline" className="border-white/20 text-white/70">{tag}</Badge>
        ))}
        {c.assigned_to && (
          <span className="text-xs text-white/50">Assigned</span>
        )}
        <span className="text-xs text-white/40 font-mono-alt ml-auto">Updated {fmtDate(c.updated_at || c.created_at)}</span>
      </div>

      <CaseDetails caseType={caseType} c={c} />

      {isStaff && (
        <div className="grid md:grid-cols-2 gap-4 rounded-lg border border-white/10 p-4 bg-white/[0.02]">
          <div>
            <Label className="text-white/60 text-xs">Status</Label>
            <Select value={c.status} onValueChange={(v) => patchCase({ status: v })} disabled={saving}>
              <SelectTrigger className="mt-1 bg-white/5 border-white/15 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/60 text-xs">Assigned to</Label>
            <Select
              value={c.assigned_to || "_none"}
              onValueChange={(v) => patchCase({ assigned_to: v === "_none" ? null : v })}
              disabled={saving}
            >
              <SelectTrigger className="mt-1 bg-white/5 border-white/15 text-white">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Unassigned</SelectItem>
                {assignees.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-white/60 text-xs">Tags (comma-separated)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                className="bg-white/5 border-white/15 text-white"
              />
              <Button
                type="button"
                className="bg-[#D4AF37] text-black shrink-0"
                disabled={saving}
                onClick={() =>
                  patchCase({
                    tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
                  })
                }
              >
                Save tags
              </Button>
            </div>
          </div>
          {caseType === "legal_request" && (
            <div className="md:col-span-2">
              <Label className="text-white/60 text-xs">Internal admin notes</Label>
              <Textarea
                defaultValue={c.admin_notes || ""}
                className="mt-1 bg-white/5 border-white/15 text-white min-h-[80px]"
                disabled={saving}
                onBlur={(e) => {
                  if (e.target.value !== (c.admin_notes || "")) {
                    patchCase({ admin_notes: e.target.value });
                  }
                }}
              />
            </div>
          )}
        </div>
      )}

      <div>
        <h4 className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono-alt mb-2">{cw.evidenceTitle || "Evidence checklist"}</h4>
        <ul className="space-y-2">
          {(c.evidence_checklist || []).map((item) => (
            <li key={item.key} className="flex items-start gap-3 text-sm">
              <Checkbox
                checked={!!item.uploaded}
                disabled={!isStaff}
                onCheckedChange={(v) => isStaff && toggleChecklist(item.key, !!v)}
                className="mt-0.5"
              />
              <div>
                <span className={item.uploaded ? "text-emerald-300" : "text-white/80"}>
                  {checklistLabels[item.key] || item.label}
                  {item.required && <span className="text-rose-400 ml-1">*</span>}
                </span>
                {item.storage_path && (
                  <a
                    href={`${API_BASE}/files/${item.storage_path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-xs text-[#D4AF37] hover:underline mt-0.5"
                  >
                    View file
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Tabs defaultValue="timeline">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="timeline">{cw.statusTimeline || "Status timeline"}</TabsTrigger>
          <TabsTrigger value="comments">
            {cw.comments || "Comments"} ({publicComments.length}{isStaff ? ` + ${internalComments.length} internal` : ""})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="timeline" className="mt-4">
          <StatusTimeline timeline={timeline} statuses={statuses} />
          {timeline.filter((ev) => ev.event_type !== "status_change" && ev.event_type !== "created").length > 0 && (
            <div className="mt-6 pt-4 border-t border-white/10 space-y-2">
              <h5 className="text-[10px] uppercase tracking-widest text-white/45 font-mono-alt">Other activity</h5>
              {timeline
                .filter((ev) => ev.event_type !== "status_change" && ev.event_type !== "created")
                .map((ev) => (
                  <div key={ev.id} className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
                    <div className="text-white/85">{ev.message}</div>
                    <div className="text-[10px] text-white/45 mt-1 font-mono-alt">
                      {ev.actor_name} · {fmtDate(ev.created_at)}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="comments" className="mt-3 space-y-3">
          {publicComments.length === 0 && !isStaff && internalComments.length === 0 && (
            <p className="text-white/45 text-sm">{cw.noComments || "No comments yet. CHINTAMAT staff will reply here."}</p>
          )}
          {publicComments.map((cm) => (
            <div key={cm.id} className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
              <div className="flex justify-between text-[10px] text-white/45 font-mono-alt mb-1">
                <span>{cm.author_name} · {isStaff ? "public" : "CHINTAMAT"}</span>
                <span>{fmtDate(cm.created_at)}</span>
              </div>
              <p className="text-white/85 whitespace-pre-wrap">{cm.body}</p>
            </div>
          ))}
          {isStaff &&
            internalComments.map((cm) => (
              <div key={cm.id} className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm">
                <div className="flex justify-between text-[10px] text-amber-200/70 font-mono-alt mb-1">
                  <span>{cm.author_name} · internal</span>
                  <span>{fmtDate(cm.created_at)}</span>
                </div>
                <p className="text-white/85 whitespace-pre-wrap">{cm.body}</p>
              </div>
            ))}
          <div className="border-t border-white/10 pt-3 space-y-2">
            <p className="text-xs text-white/50">
              {isStaff ? cw.staffCommentHint : cw.userCommentHint}
            </p>
            {isStaff && (
              <Select value={commentVis} onValueChange={setCommentVis}>
                <SelectTrigger className="w-[200px] bg-white/5 border-white/15 text-white h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">{cw.publicVis || "Public (user can see)"}</SelectItem>
                  <SelectItem value="internal">{cw.internalVis || "Internal only"}</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Textarea
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder={isStaff ? "Reply to user or add internal note…" : "Ask a question or add information…"}
              className="bg-white/5 border-white/15 text-white min-h-[72px]"
            />
            <Button
              type="button"
              className="bg-[#D4AF37] text-black"
              disabled={saving || !commentBody.trim()}
              onClick={postComment}
            >
              {isStaff ? cw.postComment || "Post comment" : cw.sendMessage || "Send message"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CaseWorkspace;
